'use client';

import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { generateUuid } from '@lib/uuid';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';

export enum CheckboxType {
  CHECKBOX = 'checkbox',
  SWITCH = 'switch',
}

export enum CheckboxSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export enum CheckboxState {
  CHECKED = 'checked',
  UNCHECKED = 'unchecked',
}

type CheckboxStyles = {
  [key in CheckboxState]: string;
};

interface CheckboxProps {
  checked: boolean;
  id?: string;
  onChange?: (checked: boolean) => void;
  boxSize?: CheckboxSize;
  label?: string;
  disabled?: boolean;
  hasError?: boolean;
  labelSmall?: boolean;
  type?: CheckboxType;
  controlBgColor?: string;
  overlayBgColor?: string;
  activeControlBgColor?: string;
  activeOverlayBgColor?: string;
  activeControlBorderColor?: string;
  addLabelClassNames?: string;
  tooltip?: string;
  className?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  id: propsId,
  checked: propsChecked,
  onChange,
  label,
  disabled,
  boxSize,
  hasError,
  labelSmall,
  type = CheckboxType.CHECKBOX,
  controlBgColor = 'border-gray-400',
  overlayBgColor = 'bg-[#b9eae4]',
  activeControlBgColor = 'bg-teal-500',
  activeControlBorderColor = 'border-teal-500',
  addLabelClassNames = '',
  tooltip = '',
  className,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(propsChecked);

  useEffect(() => {
    setIsChecked(propsChecked);
  }, [propsChecked]);

  const id = propsId || generateUuid();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const checked = event.target.checked;
    setIsChecked(checked);
    if (onChange) {
      onChange(checked);
    }
  };

  //TODO import color to theme settings
  const errorControlBgColor = 'bg-red-500';
  const errorCheckboxBorderColor = 'border-red-500';
  const getCheckboxSize = (type: CheckboxType, size?: CheckboxSize) => {
    const styles = {
      [`${CheckboxType.SWITCH}_${CheckboxSize.SM}`]: 'w-8 h-4',
      [`${CheckboxType.SWITCH}_${CheckboxSize.MD}`]: 'w-10 h-4',
      [`${CheckboxType.SWITCH}_${CheckboxSize.LG}`]: 'w-12 h-6',
      [`${CheckboxType.CHECKBOX}_${CheckboxSize.SM}`]: 'w-4 h-4',
      [`${CheckboxType.CHECKBOX}_${CheckboxSize.MD}`]: 'w-4 h-4',
      [`${CheckboxType.CHECKBOX}_${CheckboxSize.LG}`]: 'w-4 h-4',
      [CheckboxType.SWITCH]: 'w-10 h-4',
      [CheckboxType.CHECKBOX]: 'w-4 h-4',
    };

    return size ? styles[`${type}_${size}`] : styles[type];
  };

  //TODO import color to theme settings
  const disabledBgColor = 'bg-gray-300';
  const disabledBorderColor = 'border-gray-300';
  const disabledTextColor = 'text-gray-300';
  const overlayErrorBgColor = 'bg-red-200';
  const errorBgColor = 'bg-red-500';
  const errorTextColor = 'text-red-500';
  const customLblStyles = 'text-[12px]';
  const getCheckboxStyles = (type: CheckboxType, sizeComponent?: CheckboxSize): CheckboxStyles => {
    const commonStyles = {
      [CheckboxState.UNCHECKED]: `${getCheckboxSize(type, boxSize)} rounded-full ${
        hasError ? overlayErrorBgColor : disabledBgColor
      }`,
      [CheckboxState.CHECKED]: `${getCheckboxSize(type, boxSize)} rounded-full ${
        hasError ? overlayErrorBgColor : overlayBgColor
      }`,
    };

    const styles = {
      [CheckboxType.CHECKBOX]: {
        [CheckboxState.UNCHECKED]: `${commonStyles[CheckboxState.UNCHECKED]} border-2 rounded-sm bg-white ${
          disabled ? disabledBorderColor : hasError ? 'border-red-500' : controlBgColor
        }`,
        [CheckboxState.CHECKED]: `${commonStyles[CheckboxState.CHECKED]} border-2 rounded-sm ${
          hasError
            ? `${errorControlBgColor} ${errorCheckboxBorderColor}`
            : disabled
            ? `${disabledBorderColor} ${disabledBgColor}`
            : `${activeControlBorderColor} ${activeControlBgColor}`
        }`,
      },
      [CheckboxType.SWITCH]: {
        ...commonStyles,
      },
      [`${CheckboxType.SWITCH}_${CheckboxSize.LG}`]: {
        ...commonStyles,
      },
      [`${CheckboxType.SWITCH}_${CheckboxSize.SM}`]: {
        ...commonStyles,
      },
      [`${CheckboxType.SWITCH}_${CheckboxSize.MD}`]: {
        ...commonStyles,
      },
    };
    return sizeComponent ? styles[`${type}_${sizeComponent}`] : styles[type];
  };

  const checkboxStyles = getCheckboxStyles(type, boxSize);
  const transform = 'transition duration-500 transform';
  const size = getCheckboxSize(type, boxSize);
  const isSwitchType = (type: CheckboxType) => type === CheckboxType.SWITCH;

  const labelClassName = classNames(
    'whitespace-nowrap pl-2 overflow-hidden text-ellipsis',
    {
      [disabledTextColor]: disabled,
      [errorTextColor]: hasError,
      [customLblStyles]: labelSmall,
      'cursor-pointer': !disabled,
    },
    addLabelClassNames
  );

  return (
    <div className={classNames('flex items-center', className)}>
      <input
        id={id}
        type="checkbox"
        className={`${size} z-10 opacity-0 ${!disabled ? 'cursor-pointer' : ''}`}
        checked={isChecked}
        onChange={handleChange}
        disabled={disabled}
      />
      <div
        className={`flex items-center justify-center absolute ${transform} ${
          checkboxStyles[isChecked && !disabled ? CheckboxState.CHECKED : CheckboxState.UNCHECKED]
        }`}
      >
        {type === CheckboxType.CHECKBOX && isChecked && (
          <span className="text-white">
            <Icon icon={IconsMap.CHECK} className="w-4 h-4" />
          </span>
        )}
        {isSwitchType(type) && isChecked && (
          <span
            className={classNames({
              'w-3 h-3 -mr-[16px]': boxSize === CheckboxSize.SM,
              'w-5 h-5 -mr-[23px]': boxSize !== CheckboxSize.SM,
              'bg-gray-400': disabled,
              [activeControlBgColor]: !hasError && !disabled,
              [errorBgColor]: hasError,
              'rounded-full shadow-md': true,
              [transform]: true,
            })}
          ></span>
        )}
        {isSwitchType(type) && !isChecked && (
          <span
            className={classNames({
              'w-3 h-3 -ml-[16px]': boxSize === CheckboxSize.SM,
              'w-5 h-5 -ml-[23px]': boxSize !== CheckboxSize.SM,
              [errorBgColor]: hasError,
              'bg-white': !hasError,
              'rounded-full shadow-md': true,
              [transform]: true,
            })}
          ></span>
        )}
      </div>
      {label && (
        <label title={tooltip} htmlFor={id} className={labelClassName}>
          {label}
        </label>
      )}
    </div>
  );
};
